import {
  type ReactNode,
  useCallback,
  useLayoutEffect,
  useRef,
  useState,
} from "react";
import { useTranslation } from "react-i18next";
import { IconInfoCircle } from "@tabler/icons-react";
import RoomLogo from "../../../components/room/RoomLogo";
import { Avatar } from "../../../components/ui/Avatar";
import { RoomSupportedByChip } from "../../../components/room/RoomSupportedByChip";
import { getRoomLogoUrl } from "../utils/getRoomLogoUrl";
import { useApiQuery } from "../../../services/api";
import { getRoomName, getRoomDescription } from "./useRoomName";
import ExpandableText from "../../../components/app/ExpandableText";
import { useActiveLang } from "../../app/hooks/useActiveLang";
import { useCurrentUser } from "../../user/hooks/useCurrentUser";
import { contactLineKind } from "../../partners/roomWelcome";
import { gaEventRoomInfoOpened } from "../../../tools/analytics/domains/partners.analytics";

function getRoomInitials(name: string): string {
  const trimmed = name.trim();
  if (trimmed.length === 0) return "";
  const words = trimmed.split(/\s+/);
  const first = words[0] ?? "";
  const second = words.length > 1 ? (words[1] ?? "") : "";
  return (first.charAt(0) + second.charAt(0)).toUpperCase();
}

/**
 * Shared scroll-based room info header logic used by RoomDiscoveryPage and RoomResourcesPage.
 *
 * Returns:
 * - `roomInfoRef`: attach to the room info section div
 * - `hasScrolledPastInfo`: true when the section has scrolled out of view
 * - `handleScroll`: attach to the scrollable container's onScroll
 * - `RoomInfoSection`: the full-size room info block (name, attribution, logo, footer slot)
 * - `CollapsedHeaderContent`: the compact header content shown on scroll
 */
export function useRoomInfoHeader(
  roomId: string | null,
  options: { footer: ReactNode; onInfoClick: () => void }
): {
  roomInfoRef: React.RefObject<HTMLDivElement>;
  hasScrolledPastInfo: boolean;
  handleScroll: () => void;
  RoomInfoSection: React.ReactElement | null;
  CollapsedHeaderContent: React.ReactElement | null;
} {
  const { t } = useTranslation();
  const roomQuery = useApiQuery(
    "/webapp/private/rooms/:id",
    roomId !== null ? { param: { id: roomId } } : false
  );
  const currentRoom = roomQuery.state === "success" ? roomQuery.body : null;
  const { currentUser } = useCurrentUser();
  // ROOM-TEXT language: the reader explicit in-room choice, else their interface
  // locale. This header renders the CURRENT room own text, so it follows the in-room
  // translate control — which `activeLang` did not. Before this change both the name
  // and the description read `activeLang`, so switching the picker to French changed
  // neither: English copy under a French content selection.
  //
  // NOT `contentLang`: that falls back to an fr/en guess, which would serve a German
  // reader English text in a German room. See `useActiveLang`.
  //
  // `useRoomName` used to hide this choice behind a hook that hardcoded `activeLang`;
  // it is gone, and every site now names its axis. See the axis table in the PR.
  const { roomTextLang } = useActiveLang();
  const roomName =
    currentRoom === null ? "" : getRoomName(currentRoom, roomTextLang);
  const roomDescription =
    currentRoom === null ? null : getRoomDescription(currentRoom, roomTextLang);
  const [hasScrolledPastInfo, setHasScrolledPastInfo] = useState(false);
  // WHICH room the expansion belongs to, not a boolean. Expanded is then DERIVED, so
  // leakage across rooms is structurally impossible instead of corrected after the
  // fact: these room routes are reused without remounting (a link from one room to
  // another navigates discovery→discovery), and a boolean reset in an effect would still leave one
  // frame in which the new room paints expanded — with the scroll handler frozen.
  // Same reasoning as the derived active language tab in the admin room form.
  const [expandedRoomId, setExpandedRoomId] = useState<string | null>(null);
  const isDescriptionExpanded = roomId !== null && expandedRoomId === roomId;
  const roomInfoRef = useRef<HTMLDivElement>(null);

  const handleScroll = useCallback(() => {
    // While the description is expanded, the collapse toggle is frozen.
    //
    // `roomInfoRef` wraps the section that CONTAINS the description, so expanding it
    // moves the very rect this function measures. No threshold can fix that: an
    // expansion adds 150px on one room and 600px on another, so any constant is
    // wrong for some content. Widening the gap would only have moved the failure to
    // a longer description while reading as a fix.
    //
    // Freezing is bounded and content-length independent — it cannot oscillate,
    // because the input stops being read while the thing that perturbs it is active.
    // Cost: with the description expanded, scrolling down does not bring up the
    // compact header. That is predictable, reverses the moment the reader collapses
    // the text, and is a far better trade than an invented constant.
    // `roomDescription !== null` is not redundant: on a room with NO description
    // there is no toggle to collapse, so a stuck expanded state would freeze this
    // permanently — the compact header and its Join CTA could never appear, with no
    // way back short of a reload. Deriving `isDescriptionExpanded` from
    // `expandedRoomId` already makes that unreachable; this is the second mechanism,
    // because an invisible unrecoverable failure deserves more than one.
    if (roomDescription !== null && isDescriptionExpanded) return;
    if (!roomInfoRef.current) return;
    const rect = roomInfoRef.current.getBoundingClientRect();
    // Hysteresis: exit threshold (150) > enter threshold (100) by more than the
    // banner height (~40px) to prevent a feedback loop where adding/removing the
    // banner in the header shifts content and re-triggers the toggle. Unchanged —
    // the expansion is handled by the freeze above, not by this gap.
    const threshold = hasScrolledPastInfo ? 150 : 100;
    setHasScrolledPastInfo(rect.bottom < threshold);
  }, [hasScrolledPastInfo, isDescriptionExpanded, roomDescription]);

  // Re-measure the moment the description collapses. While frozen, `handleScroll`
  // ignored every scroll event, so `hasScrolledPastInfo` can be arbitrarily stale by
  // the time the reader collapses the text — expand, scroll well past the section,
  // collapse, and without this the compact header (and its Join CTA) stays hidden
  // until the next scroll gesture. `useLayoutEffect` so the correction lands in the
  // same paint as the collapse rather than flashing the wrong header for a frame.
  useLayoutEffect(() => {
    if (isDescriptionExpanded) return;
    handleScroll();
  }, [isDescriptionExpanded, handleScroll]);

  const RoomInfoSection = currentRoom ? (
    <div
      ref={roomInfoRef}
      className="bg-white flex flex-col gap-4 overflow-hidden px-4 pb-6 pt-16"
      style={{ paddingTop: "calc(4rem + var(--safe-area-top))" }}
    >
      <div className="flex flex-col gap-1">
        <div className="flex gap-2 items-center">
          <h1 className="m-0 flex-1 text-[22px] font-bold leading-[1.4] text-primary-black">
            {roomName}
          </h1>
          {(() => {
            const logoUrl = getRoomLogoUrl(currentRoom);
            return logoUrl.length > 0 ? (
              <Avatar
                variant="image"
                src={logoUrl}
                alt={roomName}
                imageFit="scale-down"
                size="lg"
                badge={null}
              />
            ) : (
              <Avatar
                variant="initials"
                initials={getRoomInitials(roomName)}
                size="lg"
                badge={null}
              />
            );
          })()}
        </div>
        {/* Chip and info icon are one target: the attribution is the question
            ("who is Biocodex to this room?") and the explainer is the answer,
            so the whole line has to be the way in — not just the 16px icon. */}
        <button
          type="button"
          aria-label={t("What is a Room?")}
          onClick={() => {
            const isMember =
              currentUser !== null &&
              currentUser.rooms.includes(currentRoom.id);
            gaEventRoomInfoOpened({
              roomId: currentRoom.id,
              isMember,
              contactLine: contactLineKind(
                currentRoom.partnerContact,
                isMember
              ),
            });
            options.onInfoClick();
          }}
          className="flex gap-1 items-center self-start cursor-pointer border-none bg-transparent p-0 text-left"
        >
          <RoomSupportedByChip
            organisationName={currentRoom.organisationModel.name}
            size="md"
          />
          <span className="flex size-6 shrink-0 items-center justify-center text-ds-slate-400">
            <IconInfoCircle className="size-4" stroke={1.25} />
          </span>
        </button>
      </div>
      {/* Clamped to 2 lines with a Read more / Voir plus affordance. The clamp stays
          CSS (`-webkit-line-clamp` via ClampLines) rather than a JS substring, so the
          full string is always in the DOM — selectable, findable by in-page search,
          and readable by assistive tech even while collapsed. The affordance only
          renders when `measureOverflow` says the text actually overflows, so a short
          description looks exactly as it did before. */}
      {roomDescription !== null ? (
        <div className="text-sm font-normal leading-[1.4] text-ds-slate-500">
          <ExpandableText
            isExpanded={isDescriptionExpanded}
            setExpand={(expanded) => {
              setExpandedRoomId(expanded ? roomId : null);
            }}
            lines={2}
          >
            {roomDescription}
          </ExpandableText>
        </div>
      ) : null}
      {options.footer}
    </div>
  ) : null;

  const CollapsedHeaderContent =
    hasScrolledPastInfo && currentRoom ? (
      <div className="flex gap-2 justify-center items-center flex-1 min-w-0 w-full px-2 animate-fade-in">
        <RoomLogo
          logoUrl={getRoomLogoUrl(currentRoom)}
          style={{
            width: 30,
            height: 30,
            minWidth: 30,
            minHeight: 30,
          }}
        />
        <div className="flex flex-col min-w-0">
          <span className="text-[11px] font-semibold leading-[1.4] text-primary-black truncate">
            {roomName}
          </span>
          <span className="text-[9px] font-normal leading-[1.4] text-primary-black truncate">
            {t("By").toLowerCase()} {currentRoom.organisationModel.name}
          </span>
        </div>
      </div>
    ) : null;

  return {
    roomInfoRef,
    hasScrolledPastInfo,
    handleScroll,
    RoomInfoSection,
    CollapsedHeaderContent,
  };
}
